// Cross-writer single-writer refresh-token rotation lock (ios/BOUNDARY.md
// §2.10, docs/09 §9).
//
// Rotating refresh tokens are one-time: spending the same token twice is a
// SECURITY EVENT (reuse detection kills the whole session family), not a retry.
// Every platform has more than one actor holding that token:
//
//   iOS      brain (JSC) + the Swift ColdSignalingClient
//   Android  brain WebView + the native stay-reachable listener
//   Web      every open TAB + the service worker's headless decline
//            (app/src/sw/sw.ts spends and re-persists the token with no window)
//
// so the lock lives next to the storage they share rather than inside any one
// of them.
//
// ADVISORY, deliberately. Two properties matter more than mutual exclusion:
//   1. It never deadlocks a call. After a bounded wait the caller proceeds
//      regardless (Connection > everything) and a holder that dies mid-hold is
//      stolen from after the TTL — a killed tab costs the next refresh one
//      bounded stall, never a permanent block.
//   2. It is not atomic and does not pretend to be. `KvStore` has no
//      compare-and-set (get and put are separate IndexedDB transactions), so
//      two writers waking together can both observe "free" and both proceed.
//      The lock NARROWS the window; what actually protects the token is that
//      holders re-read the freshest stored token INSIDE the lock and recover by
//      retrying with whatever storage holds when a refresh is rejected. Widening
//      KvStore to support CAS would ripple into the native KV plugins on both
//      platforms — not worth it for a window this small.

import type { KvStore } from './kv.js';

/** The KV slot. Read by the brain AND by the web service worker — a rename
 *  here silently un-serializes them, so it lives in exactly one place. */
export const TOKEN_ROTATION_LOCK_KEY = 'brain.tokenRotationLock';

/** How long a taken lock stays valid before another writer may steal it. */
export const LOCK_TTL_MS = 15_000;
/** How long a waiter blocks before proceeding WITHOUT the lock. Kept under the
 *  TTL so a waiter never outlives the holder it is waiting for. */
export const LOCK_WAIT_CAP_MS = 8_000;
export const LOCK_POLL_MS = 150;

export interface LockRecord {
  holder: string;
  expiresAt: number;
}

export interface RotationLockOptions {
  now?: () => number;
  ttlMs?: number;
  /** Bound on `acquire()`. The web wants this well under
   *  TOKEN_ACQUISITION_BUDGET_MS (lib/http.ts) so waiting for the lock cannot
   *  by itself blow the caller's whole token budget. */
  waitCapMs?: number;
  pollMs?: number;
  /** Injectable for tests; defaults to a fresh per-instance random id. */
  holderId?: string;
}

export function randomHolderId(prefix = 'brain'): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${prefix}-${Math.random().toString(36).slice(2)}`;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class RotationLock {
  private readonly kv: KvStore;
  private readonly holderId: string;
  private readonly ttlMs: number;
  private readonly waitCapMs: number;
  private readonly pollMs: number;
  private readonly nowFn: () => number;

  constructor(kv: KvStore, options: RotationLockOptions = {}) {
    this.kv = kv;
    this.holderId = options.holderId ?? randomHolderId();
    this.ttlMs = options.ttlMs ?? LOCK_TTL_MS;
    this.waitCapMs = options.waitCapMs ?? LOCK_WAIT_CAP_MS;
    this.pollMs = options.pollMs ?? LOCK_POLL_MS;
    this.nowFn = options.now ?? (() => Date.now());
  }

  /** Take the lock, or give up and proceed anyway once the wait cap expires.
   *  Never rejects: a storage failure must not block a token refresh. */
  async acquire(): Promise<void> {
    const deadline = this.nowFn() + this.waitCapMs;
    for (;;) {
      const rec = await this.kv
        .get<LockRecord>(TOKEN_ROTATION_LOCK_KEY)
        .catch(() => undefined);
      const now = this.nowFn();
      const heldByOther = rec && rec.holder !== this.holderId && rec.expiresAt > now;
      if (!heldByOther) {
        await this.kv
          .put(TOKEN_ROTATION_LOCK_KEY, { holder: this.holderId, expiresAt: now + this.ttlMs })
          .catch(() => undefined);
        return;
      }
      if (now >= deadline) return; // advisory — never deadlock a call
      await delay(this.pollMs);
    }
  }

  /** Release only OUR record: a holder whose lock was stolen after the TTL must
   *  not delete the thief's. */
  async release(): Promise<void> {
    const rec = await this.kv.get<LockRecord>(TOKEN_ROTATION_LOCK_KEY).catch(() => undefined);
    if (rec && rec.holder === this.holderId) {
      await this.kv.delete(TOKEN_ROTATION_LOCK_KEY).catch(() => undefined);
    }
  }

  /** Run `fn` under the lock, releasing on every exit path. */
  async withLock<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      await this.release();
    }
  }
}
